import type { ComponentType } from 'react';

import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import type { SvgIconProps } from '@mui/material';
import type { TFunction } from 'i18next';

import type { ConversationChannelLike, ConversationDeliveryStatusLike } from './types';

const KNOWN_REASONS = [
  'BOT_BLOCKED',
  'EXPIRED',
  'RATE_LIMITED',
  'BOT_NOT_CONFIGURED',
  'CHAT_NOT_FOUND',
  'INVALID_PAYLOAD',
  'API_ERROR',
  'TIMEOUT',
  'RETRIES_EXHAUSTED',
];

const ICON: Record<ConversationDeliveryStatusLike, ComponentType<SvgIconProps>> = {
  QUEUED: ScheduleRoundedIcon,
  SENT: DoneRoundedIcon,
  FAILED: AutorenewRoundedIcon,
  PERMANENTLY_FAILED: ErrorOutlineRoundedIcon,
};

const COLOR: Record<ConversationDeliveryStatusLike, string> = {
  QUEUED: 'text.secondary',
  SENT: 'success.main',
  FAILED: 'warning.main',
  PERMANENTLY_FAILED: 'error.main',
};

function reasonText(t: TFunction, reason?: string | null): string | undefined {
  if (!reason) {
    return undefined;
  }
  return KNOWN_REASONS.includes(reason) ? t(`delivery.reasons.${reason}`) : reason;
}

/**
 * The delivery glyph's label + the FAILED/PERMANENTLY_FAILED secondary caption (design §11.5) — a pure
 * description so a caller can render the icon and the secondary line in two different places (the
 * meta-line glyph vs. the under-bubble caption) without instantiating the icon twice. Kept in its own
 * module (not `DeliveryStatusIcon.tsx`) so that component file exports components only — mixing a
 * plain function export into a component file breaks React Fast Refresh.
 */
export function describeDeliveryStatus(
  status: ConversationDeliveryStatusLike | null | undefined,
  reason: string | null | undefined,
  attempts: number | null | undefined,
  channel: ConversationChannelLike,
  t: TFunction,
): { label: string; secondary?: string; color: string; Icon: ComponentType<SvgIconProps> } | null {
  if (!status) {
    return null;
  }
  const reasonLabel = reasonText(t, reason);
  const count = attempts ?? 1;
  const Icon = ICON[status] ?? ScheduleRoundedIcon;
  const color = COLOR[status] ?? 'text.secondary';

  if (status === 'FAILED') {
    const secondary = reasonLabel
      ? t('delivery.retrying', { count, reason: reasonLabel })
      : t('delivery.retryingNoReason', { count });
    return { label: secondary, secondary, color, Icon };
  }
  if (status === 'PERMANENTLY_FAILED') {
    const secondary = reasonLabel
      ? t('delivery.notDelivered', { reason: reasonLabel })
      : t('delivery.notDeliveredNoReason');
    return { label: secondary, secondary, color, Icon };
  }
  if (status === 'SENT') {
    return {
      label: t('delivery.SENT', { channel: t(`channel.${channel}`, channel) }),
      color,
      Icon,
    };
  }
  return { label: t('delivery.QUEUED'), color, Icon };
}
